// @ts-check
// Desfazer e refazer do editor de e-mail (spec/email.md seção 19.2): até 50 passos sobre o valor inteiro do editor
// (documento, assunto, texto de prévia). Digitação seguida no mesmo campo vira um passo só (`coalesceKey` igual dentro
// de 1 s), para o Ctrl+Z não desfazer letra por letra. As funções são puras; o gancho guarda a pilha na tela.
import { useEffect, useRef, useState } from 'preact/hooks';
import { isEditableElement } from '../../lib/utils.js';

export const EMAIL_HISTORY_LIMIT = 50;
const COALESCE_MILLISECONDS = 1000;

/**
 * @template TValue
 * @typedef {{ past: TValue[], present: TValue, future: TValue[], lastKey: string | null, lastAt: number }} EmailHistory
 */

/** @template TValue @param {TValue} present @returns {EmailHistory<TValue>} */
export function createEmailHistory(present) {
  return { past: [], present, future: [], lastKey: null, lastAt: 0 };
}

/**
 * Passo novo. Com a mesma chave do anterior e dentro de 1 s, troca o presente sem empilhar.
 * @template TValue
 * @param {EmailHistory<TValue>} history @param {TValue} next
 * @param {{ coalesceKey?: string | null, now?: number }} [options]
 * @returns {EmailHistory<TValue>}
 */
export function pushEmailHistory(history, next, options = {}) {
  if (next === history.present) return history;
  const now = options.now ?? Date.now();
  const key = options.coalesceKey ?? null;
  if (key && key === history.lastKey && now - history.lastAt < COALESCE_MILLISECONDS) {
    return { ...history, present: next, future: [], lastAt: now };
  }
  const past = [...history.past, history.present].slice(-EMAIL_HISTORY_LIMIT);
  return { past, present: next, future: [], lastKey: key, lastAt: now };
}

/** @template TValue @param {EmailHistory<TValue>} history @returns {EmailHistory<TValue>} */
export function undoEmailHistory(history) {
  if (history.past.length === 0) return history;
  const previous = history.past[history.past.length - 1];
  return { past: history.past.slice(0, -1), present: previous, future: [history.present, ...history.future], lastKey: null, lastAt: 0 };
}

/** @template TValue @param {EmailHistory<TValue>} history @returns {EmailHistory<TValue>} */
export function redoEmailHistory(history) {
  if (history.future.length === 0) return history;
  const [next, ...future] = history.future;
  return { past: [...history.past, history.present].slice(-EMAIL_HISTORY_LIMIT), present: next, future, lastKey: null, lastAt: 0 };
}

/**
 * Pilha de desfazer da tela, com Ctrl/Cmd+Z e Ctrl/Cmd+Shift+Z (ou Ctrl+Y) fora dos campos de texto (dentro deles vale
 * o desfazer do navegador, que conhece o cursor).
 * @template TValue
 * @param {TValue} initialValue
 * @param {{ enabled?: boolean, onExternalChange?: (value: TValue) => void }} [options]
 */
export function useEmailHistory(initialValue, options = {}) {
  const [history, setHistory] = useState(() => createEmailHistory(initialValue));
  const historyRef = useRef(history);
  historyRef.current = history;
  const enabled = options.enabled !== false;

  /** @param {TValue} next @param {{ coalesceKey?: string | null }} [changeOptions] */
  const set = (next, changeOptions) => setHistory((current) => pushEmailHistory(current, next, changeOptions));
  const undo = () => setHistory((current) => undoEmailHistory(current));
  const redo = () => setHistory((current) => redoEmailHistory(current));
  /** Valor vindo de fora (gravação no servidor, outro modo): recomeça a pilha. @param {TValue} value */
  const reset = (value) => setHistory(createEmailHistory(value));

  useEffect(() => {
    if (!enabled) return undefined;
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      if (isEditableElement(/** @type {Element | null} */ (event.target))) return;
      const key = event.key.toLowerCase();
      if (key === 'z' && !event.shiftKey) {
        if (historyRef.current.past.length === 0) return;
        event.preventDefault();
        undo();
      } else if ((key === 'z' && event.shiftKey) || key === 'y') {
        if (historyRef.current.future.length === 0) return;
        event.preventDefault();
        redo();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);

  return {
    value: history.present,
    set,
    undo,
    redo,
    reset,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
  };
}
